import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SearchInput } from '@c2fo/liquidity';
import { TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import NoDataState from '@/enterprise/components/NoDataState';
import { Table, TableBody, TableDisplaySkeletonRows, TablePagination } from '@/enterprise/components/Table';
import { InvoiceDownloadMenuLoading } from '@/enterprise/features/invoiceTables/shared/InvoiceDownloadMenu';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { buildCursorResetKey, useCursorPagination } from '@/utils/useCursorPagination';
import { useSearchDraft } from '@/utils/useSearchDraft';
import { useStateResetOn } from '@/utils/useStateResetOn';
import useUrlState from '@/utils/useUrlState';
import EligibleInvoiceFilters from './EligibleInvoiceFilters';
import { EligibleInvoicesTable } from './EligibleInvoicesTable';
import EligibleToolbarActions from './EligibleToolbarActions';
import {
  EligibleInvoiceActions,
  EligibleInvoiceFilterOptions,
  EligibleInvoiceSortKey,
  EligibleInvoiceSource,
  EligibleInvoiceUrlState,
} from './types';
import { mergeFilters } from './utils';

const EligibleInvoicesView = ({
  source,
  actions,
  controlsIntercomTarget,
}: {
  source: EligibleInvoiceSource;
  actions?: EligibleInvoiceActions;
  /** Optional Intercom target on the controls strip (IC sets `invoiceCentralInvoiceControls`). */
  controlsIntercomTarget?: IntercomDataTargetNames;
}) => {
  const { t } = useTranslation();
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

  const [urlState, setUrlState] = useUrlState<EligibleInvoiceUrlState>({
    pageSize: 25,
    sort: { key: 'amount', direction: 'desc' },
  });

  // Reason: every query input lives in the URL, which can change without this view unmounting
  // (back navigation, re-entering the tab). Keying the cursors and the search box on those inputs
  // keeps both in step with what was actually queried, so no handler has to remember to reset.
  const queryInputsKey = buildCursorResetKey(urlState);
  const appliedSearchValue = urlState.searchValue ?? '';

  const { currentCursor, currentPage, canGoBack, goToNextPage, goToPreviousPage } = useCursorPagination({
    resetKey: queryInputsKey,
  });
  const [nextCursor, setNextCursor] = useStateResetOn<string | null>(queryInputsKey, null);
  const { draft: searchInput, setDraft: setSearchInput, inputKey: searchInputKey } = useSearchDraft(appliedSearchValue);

  const handleSort = (sortKey: EligibleInvoiceSortKey) => {
    const updatedSortDirection: 'asc' | 'desc' = urlState.sort?.direction === 'asc' ? 'desc' : 'asc';
    if (urlState.sort && sortKey === urlState.sort.key) {
      actions?.onSort?.({ sortKey, sortDirection: updatedSortDirection });
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: updatedSortDirection,
        },
      }));
    } else {
      actions?.onSort?.({ sortKey, sortDirection: 'desc' });
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: 'desc',
        },
      }));
    }
  };

  const handleSearchValue = (search: string | undefined) => {
    // useUrlState parses digit-only values as numbers (e.g. "123" -> 123), so searchInput can be
    // a number at runtime even though it's typed as string -- cast before calling .trim()
    const trimmedSearch = search != null ? String(search).trim() : search;

    actions?.onSearch?.({ searchValue: trimmedSearch });

    setUrlState((f) => ({
      ...f,
      searchValue: trimmedSearch,
    }));
  };

  const handleNextPage = () => {
    if (nextCursor) {
      actions?.onPageChange?.();
      goToNextPage(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    actions?.onPageChange?.();
    goToPreviousPage();
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const size = Number(e.target.value);
    actions?.onPageSizeChange?.({ size });
    setUrlState((f) => ({ ...f, pageSize: size }));
  };

  const handleFilters = (newFilters?: EligibleInvoiceFilterOptions) => {
    actions?.onFiltersChange?.({ filterKeys: Object.keys(newFilters ?? {}) });

    const mergedFilters = mergeFilters({ prevFilters: urlState.filters, newFilters });
    setUrlState((f) => ({
      ...f,
      filters: mergedFilters,
    }));
  };

  const clearFilters = () => {
    setUrlState((f) => {
      const updatedState: Partial<EligibleInvoiceUrlState> = {
        ...f,
        filters: undefined,
      };

      return updatedState;
    });
  };

  return (
    <div>
      <EligibleInvoiceFilters
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        handleFilters={handleFilters}
        currency={source.currency}
        filters={urlState.filters}
        takerMarkets={source.takerMarkets}
      />
      <div
        className="flex flex-col justify-between border-y border-deepGray-500/[12%] bg-canvas sm:flex-row"
        {...(controlsIntercomTarget ? { 'data-intercom-target': controlsIntercomTarget } : {})}
      >
        <div className="flex flex-col items-center gap-3 p-4 sm:flex-row">
          <SearchInput
            key={searchInputKey}
            className="w-full sm:w-60"
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => {
              setSearchInput('');
              handleSearchValue(undefined);
            }}
            placeholder={t('invoices.searchInputPlaceholder')}
            value={searchInput}
            withSearchIcon
            onKeyDown={(e) => e.key === 'Enter' && handleSearchValue(searchInput)}
          />
          <Button
            className="w-full bg-canvas sm:w-fit"
            onClick={() => handleSearchValue(searchInput)}
            variant="secondary"
          >
            {t('core.search')}
          </Button>
        </div>
        <div className="flex items-center gap-2 p-4">
          <div className="relative">
            <Button onClick={() => setFilterOpen(true)} variant="primary">
              {t('core.filters')}
            </Button>
            {Object.keys(urlState.filters ?? {}).length > 0 && (
              <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-lightBlue-500 text-xs font-medium text-white">
                {Object.keys(urlState.filters ?? {}).length}
              </div>
            )}
          </div>
          <Button
            disabled={Object.keys(urlState.filters ?? {}).length === 0}
            onClick={clearFilters}
            variant="secondary"
          >
            {t('core.clearFilters')}
          </Button>
          {actions?.enableFilterBarDownload && (
            <QueryBoundaries LoadingComponent={InvoiceDownloadMenuLoading}>
              <EligibleToolbarActions
                source={source}
                urlState={urlState as EligibleInvoiceUrlState}
                onDownloadSubmitted={actions.onDownloadSubmitted}
              />
            </QueryBoundaries>
          )}
        </div>
      </div>
      <QueryBoundaries
        LoadingComponent={() => (
          <Table>
            <TableBody>
              <TableDisplaySkeletonRows colSpan={7} rowCount={10} />
            </TableBody>
          </Table>
        )}
        ErrorComponent={() => (
          <div className="px-4 pb-6 pt-8">
            <NoDataState
              icon={<TechnicalDifficultyIcon className="size-24 pb-4" />}
              title={t('invoices.errorLoadingInvoices')}
              message={t('invoices.errorLoadingInvoicesDescription')}
              action={
                <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                  {t('core.refresh')}
                </Button>
              }
            />
          </div>
        )}
      >
        <EligibleInvoicesTable
          currentCursor={currentCursor}
          setNextCursor={setNextCursor}
          urlState={urlState as EligibleInvoiceUrlState}
          handleSort={handleSort}
          source={source}
        />
      </QueryBoundaries>
      <TablePagination
        className="p-6"
        onNextChange={handleNextPage}
        onPrevChange={handlePreviousPage}
        onPageSizeChange={handlePageSizeChange}
        pageSize={urlState.pageSize ?? 25}
        disablePrev={!canGoBack}
        disableNext={!nextCursor}
        currentPage={currentPage}
      />
    </div>
  );
};

export default EligibleInvoicesView;
